/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useEffect } from 'react';
import BrandSwitcher from '../components/BrandSwitcher';
import DemographicFilter, { DemographicFilters } from '../components/DemographicFilter';
import ExportMenu from '../components/ExportMenu';
import { useBrandMetrics } from '../hooks/useBrandMetrics';
import { useDateFilter } from '../contexts/DateFilterContext';
import { useNavigate } from 'react-router-dom';

/**
 * AI-Driven Insights Dashboard
 * Provides executive-level insights, correlations between digital and traditional metrics,
 * and strategic recommendations for decision-makers.
 */
const InsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedDate, formatDate } = useDateFilter();
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [activeView, setActiveView] = useState<'overall' | 'executive' | 'correlations' | 'trends' | 'segments'>('overall');
  const [demographicFilters, setDemographicFilters] = useState<DemographicFilters>({
    age: [],
    gender: [],
    nationality: [],
    city: [],
    sec: [],
    decisionMaker: [],
    householdCars: [],
    residenceType: []
  });

  const { allBrands } = useBrandMetrics(selectedBrand);

  // Set default brand on load
  useEffect(() => {
    if (allBrands.length > 0 && !selectedBrand) {
      setSelectedBrand(allBrands[0].brand_id);
    }
  }, [allBrands, selectedBrand]);

  const handleAskBrandDoctor = () => {
    navigate('/chat');
  };

  const currentBrand = allBrands.find(b => b.brand_id === selectedBrand);

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">AI-Driven Insights</h1>
            <p className="text-text-muted">Strategic insights connecting digital signals with brand health</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Brand Switcher */}
            <BrandSwitcher
              brands={allBrands}
              selectedBrand={currentBrand || null}
              onBrandSelect={setSelectedBrand}
              recentBrands={[selectedBrand].filter(Boolean)}
            />

            {/* Demographic Filters */}
            <DemographicFilter onFiltersChange={setDemographicFilters} />

            {/* Export Menu */}
            <ExportMenu
              data={{}}
              dashboardElementId="insights-dashboard"
              brandName={currentBrand?.name || 'Insights'}
              exportType="all"
              filename={`${currentBrand?.name?.toLowerCase().replace(/\s+/g, '_') || 'brand'}_insights`}
            />

            {/* Ask Brand Doctor Button */}
            <button
              onClick={handleAskBrandDoctor}
              className="flex items-center gap-1.5 px-2.5 py-1 border border-border/50 rounded-full hover:border-primary/40 hover:bg-primary/5 transition-all"
            >
              <i className="fas fa-stethoscope text-text-muted text-xs"></i>
              <span className="text-foreground text-sm">Ask Brand Doctor</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mt-6 border-b border-border">
          <button
            onClick={() => setActiveView('overall')}
            className={`px-4 py-3 font-medium transition-all text-sm relative ${
              activeView === 'overall'
                ? 'text-primary'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            <i className="fas fa-th-large mr-2"></i>
            Overall
            {activeView === 'overall' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveView('executive')}
            className={`px-4 py-3 font-medium transition-all text-sm relative ${
              activeView === 'executive'
                ? 'text-primary'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            <i className="fas fa-chart-line mr-2"></i>
            Executive Summary
            {activeView === 'executive' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveView('correlations')}
            className={`px-4 py-3 font-medium transition-all text-sm relative ${
              activeView === 'correlations'
                ? 'text-primary'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            <i className="fas fa-project-diagram mr-2"></i>
            Correlations
            {activeView === 'correlations' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveView('trends')}
            className={`px-4 py-3 font-medium transition-all text-sm relative ${
              activeView === 'trends'
                ? 'text-primary'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            <i className="fas fa-chart-area mr-2"></i>
            Trends & Predictions
            {activeView === 'trends' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveView('segments')}
            className={`px-4 py-3 font-medium transition-all text-sm relative ${
              activeView === 'segments'
                ? 'text-primary'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            <i className="fas fa-users mr-2"></i>
            Segments
            {activeView === 'segments' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        </div>
      </div>
      
      {/* Main Content with Coming Soon Lock */}
      <div id="insights-dashboard" className="relative min-h-[500px]">
        {/* Blurred Content Preview */}
        <div className="filter blur-sm opacity-50 pointer-events-none">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-text-muted text-sm">Health Score</p>
                <p className="text-2xl font-bold text-foreground">78.5</p>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-text-muted text-sm">Awareness</p>
                <p className="text-2xl font-bold text-foreground">82.5%</p>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-text-muted text-sm">NPS</p>
                <p className="text-2xl font-bold text-foreground">52</p>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <p className="text-text-muted text-sm">Digital Health</p>
                <p className="text-2xl font-bold text-foreground">75.2%</p>
              </div>
            </div>
            <div className="bg-card rounded-2xl p-6 border border-border">
              <h2 className="text-xl font-semibold text-foreground mb-4">Executive Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-secondary rounded-lg p-4 h-32"></div>
                <div className="bg-background-secondary rounded-lg p-4 h-32"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-2xl p-6 border border-border h-64">
                <h3 className="text-foreground font-medium mb-3">Key Correlations</h3>
                <div className="space-y-2">
                  {['Sentiment ↔ Trust', 'Engagement ↔ Loyalty', 'SOV ↔ Awareness'].map((item) => (
                    <div key={item} className="flex justify-between">
                      <span className="text-text-muted text-sm">{item}</span>
                      <span className="text-foreground">0.85</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-card rounded-2xl p-6 border border-border h-64">
                <h3 className="text-foreground font-medium mb-3">Top Segments</h3>
                <div className="space-y-2">
                  {['25-34 • Male • Dubai', '35-44 • Female • Abu Dhabi'].map((item) => (
                    <div key={item} className="flex justify-between">
                      <span className="text-text-muted text-sm">{item}</span>
                      <span className="text-foreground">92%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coming Soon Lock Overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[2px] rounded-2xl">
          <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center">
              <i className="fas fa-clock text-primary text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">Coming Soon</h3>
            <p className="text-text-muted text-sm mb-6">
              AI-Driven Insights module is currently under development. Get executive summaries, predictive analytics, correlation analysis, and segment targeting all powered by advanced AI.
            </p>
            <a
              href="mailto:info@silainsights.com?subject=Interest in AI-Driven Insights Module"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-full font-medium text-sm hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/25"
            >
              <i className="fas fa-bell"></i>
              Notify Me When Ready
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightsPage;